import type { ChartType } from '@app/src/pages/Assessments/ChartTypeSelector'
import { AnswersGroupingType } from '@app/src/pages/ResourceCollection/Filters/StatisticsConstants'
import ReactEChartsCore from 'echarts-for-react/lib/core'
import { BarChart } from 'echarts/charts'
import { GridComponent, LegendComponent, TitleComponent, TooltipComponent } from 'echarts/components'
import * as echarts from 'echarts/core'
import { SVGRenderer } from 'echarts/renderers'
import React, { useMemo } from 'react'
import { useDataInsightsModal } from '../DataInsightsModalContext'
import { useCategorySummaryChartConfigs } from './CategoryChartsConfigs/useCategorySummaryChartConfigs'
import type { EchartClickItemData } from './OptionMultiGroupChart'
import { usePeriodSummaryChartConfigs } from './PeriodChartsConfigs/usePeriodSummaryChartConfigs'
import { useSpendSummaryChartConfigs } from './SpendChartsConfigs/useSpendSummaryChartConfigs'

export interface StatisticsSummary {
  id: number
  title: string
  resultForGroups: StatisticsSummaryPerGroup[]
}

export interface StatisticsSummaryPerGroup {
  groupName: string
  groupId?: string
  companiesAnswered: number
  companiesAnsweredDontHaveThis: number
  companiesNotAnswered: number
}

interface OptionBarProps {
  statistics: StatisticsSummary
  echartsRef: React.MutableRefObject<ReactEChartsCore | null>
  selectedChartType: ChartType
  groupBy: string
  periodNames: string[]
}

const DataHubSummaryChart: React.FC<OptionBarProps> = ({ statistics, echartsRef, selectedChartType, groupBy }) => {
  const { openDataInsightsModal } = useDataInsightsModal()

  const height = '450px'
  const resultsForGroup: StatisticsSummaryPerGroup[] = statistics.resultForGroups

  const periodOptions = usePeriodSummaryChartConfigs({ resultsForGroup, selectedChartType })
  const spendOptions = useSpendSummaryChartConfigs({ resultsForGroup, selectedChartType })
  const categoryOptions = useCategorySummaryChartConfigs({ resultsForGroup, selectedChartType, echartsRef })

  const options = useMemo(() => {
    switch (groupBy) {
      case AnswersGroupingType.SpendClassification:
        return spendOptions
      case AnswersGroupingType.PeriodName:
        return periodOptions
      default:
        return categoryOptions
    }
  }, [groupBy, spendOptions, periodOptions, categoryOptions])

  echarts.use([TitleComponent, TooltipComponent, GridComponent, BarChart, SVGRenderer, LegendComponent])

  const events = useMemo(
    () => ({
      click: ({ data }: { data: EchartClickItemData }) => {
        const userSelection = {
          answer: [data.name],
          summaryType: data.summaryType ? [data.summaryType] : [],
          groupName: data.groupName,
          groupId: data.groupId,
        }
        openDataInsightsModal(statistics, userSelection)
      },
    }),
    [openDataInsightsModal, statistics],
  )

  return (
    <ReactEChartsCore
      key={`${groupBy}-${selectedChartType}`}
      onEvents={events}
      option={options}
      echarts={echarts}
      style={{ minWidth: '100%', height }}
      ref={echartsRef}
    />
  )
}

export default DataHubSummaryChart
